import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

import { WeatherGlyph, type GlyphKind } from "../../components/WeatherGlyph.js";
import { dayRangeLabel } from "../../lib/dates.js";
import { spanMeta, spanOn, spanTime } from "../../lib/span.js";
import { KitchenControls } from "./KitchenControls.js";
import { KitchenLegend } from "./KitchenLegend.js";
import { KitchenMonth, type MonthPayload } from "./KitchenMonth.js";
import { foregroundOn, initialOf } from "../../tokens/palette.js";
import type { Occurrence } from "../../lib/types.js";

/**
 * The kitchen screen.
 *
 * Read from six to ten feet, by people holding a coffee. No touch, no
 * scrolling, no hover, and nothing below 28px. It is on twenty-four hours a
 * day in a room where people eat, so nothing animates either.
 *
 * The failure this screen must never have is looking confident and being
 * wrong, so staleness is a designed state rather than an afterthought: past
 * fifteen minutes the whole surface desaturates and says what it is showing.
 */

const POLL_MS = 60_000;
/** Spec §8: escape whatever a browser accumulates over a week on a wall. */
const RELOAD_MS = 6 * 60 * 60 * 1000;
const STALE_MS = 15 * 60 * 1000;

/*
 * How long a tapped event stays up.
 *
 * The screen's job is the day, not one thing on it, so anything covering the
 * day has to take itself away — nobody is going to walk back into the kitchen
 * to dismiss a panel. Long enough to read a note twice at ten feet.
 */
const DETAIL_MS = 45_000;

/*
 * How long the screen stays where someone left it.
 *
 * Every control here is a detour from the screen's actual job, which is to
 * show today without being asked. So browsing is temporary: two minutes after
 * the last touch it goes back to the week, this month, today. Nobody has to
 * remember to put it back, and nobody walks into the kitchen tomorrow to find
 * it still showing last November.
 */
const IDLE_MS = 120_000;

/*
 * The screen never scrolls, so a full column has to admit it rather than clip.
 * A screen quietly hiding an event is the screen lying about the day.
 *
 * Every column has the same shape: N events fit on their own, but the "+N more"
 * line costs a slot of its own, so exceeding the limit drops *two*. All the
 * numbers below were measured at 1080p rather than estimated.
 */
function fitted<T>(items: T[], alone: number, withMore: number): { shown: T[]; hidden: number } {
  const shown = items.length <= alone ? items : items.slice(0, withMore);
  return { shown, hidden: items.length - shown.length };
}

/**
 * Today's column, measured against the header as it now stands.
 *
 * 644px below the label, 126px an event, 58px the overflow line:
 *
 *   fresh   5 alone      630 <= 644
 *           4 + overflow 562 <= 644   (5 + overflow would be 688 — over)
 *
 * The stale banner costs another 108px, leaving 536:
 *
 *   stale   4 alone      504 <= 536
 *           3 + overflow 436 <= 536   (4 + overflow would be 562 — over)
 *
 * Exceeding the cap therefore drops two, not one: the overflow line costs a
 * slot of its own. Clipping an event in the state where trust is already in
 * question would be the worst possible moment for it, which is why the stale
 * numbers are the conservative ones.
 *
 * These came off the rendered page at 1080p, not off a calculator. Re-measure
 * them if anything in the header moves.
 */
const TODAY_FITS = {
  fresh: { alone: 5, withMore: 4 },
  stale: { alone: 4, withMore: 3 },
};

/** An upcoming cell: 333px of content, 50px head, 90px an event, 37px more. */
const UPCOMING_FITS = { alone: 3, withMore: 2 };

interface DisplayDay {
  date: string;
  occurrences: Occurrence[];
}

interface WeatherDay {
  date: string;
  high: number;
  low: number;
  condition: string;
  kind: GlyphKind;
  night: boolean;
  precip_chance: number;
}

interface Weather {
  forecast_date: string;
  fetched_at: string;
  stale: boolean;
  units: "imperial" | "metric";
  attribution: { text: string; url: string };
  today: WeatherDay;
  forecast: WeatherDay[];
}

interface Payload {
  generated_at: string;
  timezone: string;
  today: DisplayDay;
  upcoming: DisplayDay[];
  people: { id: string; display_name: string; color: string }[];
  /** Null when the provider is down or the key is dead. A designed state. */
  weather: Weather | null;
}

function Discs({ people, size }: { people: Occurrence["people"]; size: number }) {
  return (
    <span className="kd-discs" style={{ gap: Math.round(size * 0.22) }}>
      {people.map((person) => (
        <span
          key={person.id}
          className="disc"
          style={{
            width: size,
            height: size,
            background: person.color,
            color: foregroundOn(person.color),
            fontSize: Math.round(size * 0.5),
          }}
        >
          {initialOf(person.display_name)}
        </span>
      ))}
    </span>
  );
}

function timeOf(occurrence: Occurrence, zone: string): string {
  if (occurrence.all_day) return "all day";
  const start = DateTime.fromISO(occurrence.starts_at, { zone });
  if (occurrence.ends_at === null) return start.toFormat("h:mm a").toLowerCase();
  return `${start.toFormat("h:mm").toLowerCase()} – ${DateTime.fromISO(occurrence.ends_at, { zone }).toFormat("h:mm a").toLowerCase()}`;
}

/**
 * The rail down the left of an event that is part of a longer run.
 *
 * This screen is read from ten feet, where a line of text is a grey smudge and
 * a coloured mark is not. So before anyone reads "day 2 of 4" they have
 * already seen that the same colour runs down Tuesday, Wednesday and Thursday
 * — which is the answer to the only question the wall is ever asked.
 */
function SpanRail({
  occurrence,
  date,
  zone,
}: {
  occurrence: Occurrence;
  date: string;
  zone: string;
}): React.JSX.Element | null {
  const span = spanOn(occurrence, date, zone);
  if (!span.spans) return null;

  const classes = [
    "kd-rail",
    span.fromEarlier ? "is-from-earlier" : "",
    span.intoLater ? "is-into-later" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={classes}
      style={{ background: occurrence.people[0]?.color ?? "var(--ink)" }}
      aria-hidden="true"
    />
  );
}

export function Display(): React.JSX.Element {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [payload, setPayload] = useState<Payload | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /** When the last *successful* fetch landed — the freshness clock. */
  const [fetchedAt, setFetchedAt] = useState<DateTime | null>(null);
  const [tick, setTick] = useState(0);
  const mounted = useRef(true);

  /*
   * The composition is authored at 1920x1080 and scaled to whatever the panel
   * actually reports. This cannot be done in CSS: scale() needs a unitless
   * number, and calc(100vw / 1920) yields a length, which makes the whole
   * transform invalid and silently dropped.
   */
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fit = (): void => {
      setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  /*
   * Point "Add to Home Screen" at this screen rather than at the phone app.
   *
   * index.html links one manifest for the whole bundle, and its start_url is
   * "/". Since iPadOS 16.4 Safari honours that over the URL on screen, so
   * adding the kitchen screen to the home screen produced an icon that opened
   * the sign-in gate. The static manifest cannot be corrected in place either:
   * the right start_url contains this household's token, which only exists at
   * runtime — and the wall wants landscape where the phone wants portrait.
   *
   * Swapping the href is enough. Safari fetches whatever is linked at the
   * moment someone taps Add to Home Screen, so the icon is minted from the
   * manifest below rather than the bundle's.
   */
  useEffect(() => {
    if (token === "") return;

    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    // iOS reads this for the icon's label and prefers it over the manifest, so
    // it has to move too — otherwise the house ends up with two icons on two
    // devices both called Frankie.
    const title = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]',
    );

    const hadHref = link?.getAttribute("href") ?? null;
    const hadTitle = title?.getAttribute("content") ?? null;

    link?.setAttribute("href", `/api/display/manifest?token=${encodeURIComponent(token)}`);
    title?.setAttribute("content", "Kitchen");

    // Restored on the way out. Nothing navigates off this screen today, but
    // leaving the document describing itself as the kitchen would be a trap
    // for whatever does.
    return () => {
      if (hadHref !== null) link?.setAttribute("href", hadHref);
      if (hadTitle !== null) title?.setAttribute("content", hadTitle);
    };
  }, [token]);

  /*
   * The event someone tapped. Held by id rather than by value so a poll that
   * lands while it is open refreshes it — and closes it if the event went
   * away, because a wall screen showing a deleted event is the exact failure
   * this surface is built to avoid.
   */
  const [openedId, setOpenedId] = useState<string | null>(null);

  /** Which view the wall is showing, and where it has been steered to. */
  const [view, setView] = useState<"week" | "month">("week");
  const [month, setMonth] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [monthPayload, setMonthPayload] = useState<MonthPayload | null>(null);
  /** Bumped on every touch; the idle timer watches it. */
  const [touchedAt, setTouchedAt] = useState(0);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/display?token=${encodeURIComponent(token)}`);
      if (!response.ok) {
        if (response.status === 404) {
          setFailed("That link isn't valid. Make a new one in settings.");
        }
        return;
      }
      const data = (await response.json()) as Payload;
      if (!mounted.current) return;
      setPayload(data);
      setFetchedAt(DateTime.fromISO(data.generated_at));
      setFailed(null);
    } catch {
      // A failed poll is not an error state — the last good render stays up and
      // ages into staleness on its own, which is the honest signal.
    }
  }, [token]);

  /*
   * The six-hourly reload must not happen while someone is reading. It is a
   * housekeeping measure with no deadline; being three minutes late costs
   * nothing, and blanking the screen mid-sentence costs the one bit of trust
   * this surface has.
   */
  const openedRef = useRef(false);

  const loadMonth = useCallback(async () => {
    try {
      const query = month === null ? "" : `&month=${encodeURIComponent(month)}`;
      const response = await fetch(`/api/display/month?token=${encodeURIComponent(token)}${query}`);
      if (!response.ok) return;

      const data = (await response.json()) as MonthPayload;
      if (!mounted.current) return;
      setMonthPayload(data);
      setFetchedAt(DateTime.fromISO(data.generated_at));
    } catch {
      // Same rule as the week: a failed poll leaves the last good render up
      // and lets it age into staleness rather than blanking.
    }
  }, [token, month]);

  // The month is fetched when it is being shown, and re-fetched when someone
  // pages to a different one. No point polling forty-two days at a wall that
  // is showing this week.
  useEffect(() => {
    if (view !== "month") return;
    void loadMonth();
    const poll = setInterval(() => void loadMonth(), POLL_MS);
    poll.unref?.();
    return () => clearInterval(poll);
  }, [view, loadMonth]);

  /*
   * Back to the default after a quiet couple of minutes. The panel dismissing
   * itself is not enough: someone can leave the screen on next March, and the
   * next person into the kitchen would have no idea it was not today.
   */
  useEffect(() => {
    if (view === "week" && month === null && selectedDay === null) return;

    const timer = setTimeout(() => {
      setView("week");
      setMonth(null);
      setSelectedDay(null);
      setOpenedId(null);
    }, IDLE_MS);

    return () => clearTimeout(timer);
  }, [view, month, selectedDay, touchedAt]);

  useEffect(() => {
    mounted.current = true;
    void load();

    const poll = setInterval(() => void load(), POLL_MS);
    // Re-render once a minute so the staleness clock advances even when the
    // network is gone and nothing new is arriving.
    const clock = setInterval(() => setTick((t) => t + 1), 30_000);

    let reload: ReturnType<typeof setTimeout>;
    const armReload = (ms: number): void => {
      reload = setTimeout(() => {
        if (openedRef.current) {
          armReload(DETAIL_MS);
          return;
        }
        window.location.reload();
      }, ms);
    };
    armReload(RELOAD_MS);

    return () => {
      mounted.current = false;
      clearInterval(poll);
      clearInterval(clock);
      clearTimeout(reload);
    };
  }, [load]);

  // Takes itself away. Nobody walks back to the kitchen to close a panel.
  useEffect(() => {
    if (openedId === null) return;
    const timer = setTimeout(() => setOpenedId(null), DETAIL_MS);
    return () => clearTimeout(timer);
  }, [openedId]);

  /*
   * Resolved from the payload on every render rather than captured on tap, so
   * a poll that renames or moves the event is reflected instead of hidden.
   */
  /*
   * Looked up across both payloads, because either view can open one. Checking
   * only the week's seven days closed the panel the instant anyone tapped
   * something in a month they had paged to.
   */
  const openedFrom =
    openedId === null
      ? null
      : ([
          ...(payload === null ? [] : [payload.today, ...payload.upcoming]),
          ...(monthPayload === null ? [] : [monthPayload.today, ...monthPayload.days]),
        ].find((day) => day.occurrences.some((o) => o.occurrence_id === openedId)) ?? null);

  const opened =
    openedFrom?.occurrences.find((o) => o.occurrence_id === openedId) ?? null;
  /** The day it was tapped on, so the panel can say where in a run that is. */
  const openedDate = openedFrom?.date ?? null;

  // Gone means gone. A wall screen showing a deleted event is the exact
  // failure this surface exists to avoid.
  useEffect(() => {
    if (openedId !== null && payload !== null && opened === null) setOpenedId(null);
  }, [openedId, payload, opened]);

  openedRef.current = opened !== null;


  if (failed !== null) {
    return (
      <div className="kd kd--message">
        <p>{failed}</p>
      </div>
    );
  }

  if (payload === null || fetchedAt === null) return <div className="kd kd--blank" />;

  const zone = payload.timezone;
  const ageMs = DateTime.now().toMillis() - fetchedAt.toMillis();
  const stale = ageMs > STALE_MS;
  const ageMinutes = Math.round(ageMs / 60_000);

  const weather = payload.weather;

  /*
   * Every control goes through here. Touching anything restarts the idle
   * clock, so a two-minute browse is two minutes from the *last* touch rather
   * than from the first — otherwise the screen snaps back mid-scroll of a
   * month someone is still reading.
   */
  const touched = (act: () => void): (() => void) =>
    () => {
      act();
      setTouchedAt((n) => n + 1);
    };

  const monthShown =
    monthPayload?.month ?? month ?? DateTime.now().setZone(zone).toFormat("yyyy-LL");
  const monthDate = DateTime.fromISO(`${monthShown}-01`, { zone });
  const atThisMonth = monthShown === DateTime.now().setZone(zone).toFormat("yyyy-LL");

  const controls = (
    <KitchenControls
      view={view}
      atThisMonth={atThisMonth}
      onView={touched(() => {
        setView(view === "week" ? "month" : "week");
        setSelectedDay(null);
      })}
      onPrevMonth={touched(() => setMonth(monthDate.minus({ months: 1 }).toFormat("yyyy-LL")))}
      onNextMonth={touched(() => setMonth(monthDate.plus({ months: 1 }).toFormat("yyyy-LL")))}
      onThisMonth={touched(() => {
        setMonth(null);
        setSelectedDay(null);
      })}
    />
  );

  /*
   * Weather has its own clock. Spec §8 and the brief are firm that stale
   * weather must never make the schedule look untrustworthy, so nothing else
   * on this screen reacts to it — only the glyphs and temperatures pull back,
   * and the stamp beside the attribution says which day it is for.
   */
  const weatherFor = (date: string): WeatherDay | undefined =>
    weather === null
      ? undefined
      : weather.today.date === date
        ? weather.today
        : weather.forecast.find((d) => d.date === date);

  const todayDate = DateTime.fromISO(payload.today.date, { zone });
  const todayEvents = payload.today.occurrences;
  const todayFits = stale ? TODAY_FITS.stale : TODAY_FITS.fresh;
  const { shown: todayShown, hidden: todayHidden } = fitted(
    todayEvents,
    todayFits.alone,
    todayFits.withMore,
  );

  return (
    <div className={`kd${stale ? " is-stale" : ""}`} data-tick={tick}>
      <div className="kd-page" style={{ transform: `translate(-50%, -50%) scale(${String(scale)})` }}>
        {view === "month" ? (
          <>
          <header className="kd-mhead">
            <span className="kd-mhead__title">{monthDate.toFormat("LLLL yyyy")}</span>
            <span className="kd-stamp">
              {stale
                ? `last updated ${fetchedAt.setZone(zone).toFormat("h:mm a").toLowerCase()}`
                : `${fetchedAt.setZone(zone).toFormat("h:mm a").toLowerCase()} · live`}
            </span>

            <div className="kd-mhead__right">
              <KitchenLegend people={monthPayload?.people ?? payload.people} />
              {controls}
            </div>
          </header>

          {stale && (
            <div className="kd-banner">
              <span className="kd-banner__dot" />
              <span className="kd-banner__text">
                Showing {fetchedAt.setZone(zone).toFormat("h:mm a").toLowerCase()} — the screen lost
                the house wifi
              </span>
              <span className="kd-banner__age">retrying · {ageMinutes} min old</span>
            </div>
          )}

          {monthPayload === null ? (
            // The first month fetch is in flight. An empty grid rather than a
            // spinner: this screen never animates, and it resolves in a blink.
            <div className="kd-month" />
          ) : (
            <KitchenMonth
              payload={monthPayload}
              zone={zone}
              selected={selectedDay}
              onSelectDay={(date) => touched(() => setSelectedDay(date))()}
              onOpenEvent={(id) => touched(() => setOpenedId(id))()}
            />
          )}
          </>
        ) : (
          <>
          <header className="kd-head">
            <div className="kd-head__left">
              {/* Above the day, left justified: it is the first thing to
                  doubt when the screen looks wrong, and reading it should not
                  mean tracking to the far corner. */}
              <span className="kd-stamp">
                {stale
                  ? `last updated ${fetchedAt.setZone(zone).toFormat("h:mm a").toLowerCase()}`
                  : `${fetchedAt.setZone(zone).toFormat("h:mm a").toLowerCase()} · live`}
              </span>
              <div className="kd-head__day">
                <span className="kd-dayname">{todayDate.toFormat("cccc")}</span>
                <span className="kd-date">{todayDate.toFormat("LLLL d")}</span>
              </div>
            </div>

            <div className="kd-head__right">
              <KitchenLegend people={payload.people} />
              {controls}
            </div>
          </header>


          <div className="kd-body">
            <section className="kd-today">
              <div className="kd-today__head">
                <span className="kd-today__dow">{todayDate.toFormat("ccc")}</span>
                <span className="kd-today__num">{todayDate.toFormat("d")}</span>

                {(() => {
                  const w = weatherFor(payload.today.date);
                  if (w === undefined) return null;
                  return (
                    <span className={`kd-wx kd-wx--today${weather?.stale === true ? " is-stale" : ""}`}>
                      <WeatherGlyph kind={w.kind} size={64} night={w.night} surface="#fffdf7" />
                      {/* High and low, not the current reading. "Does anyone
                          need a coat" is a property of the day. */}
                      <span className="kd-wx__temp">
                        {w.high}° <span className="kd-wx__low">{w.low}°</span>
                      </span>
                    </span>
                  );
                })()}
              </div>
              <p className="kd-today__label">Today</p>

              {todayEvents.length === 0 ? (
                <div className="kd-quiet">
                  <p className="kd-quiet__head">
                    Nothing on
                    <br />
                    the calendar
                  </p>
                  <p className="kd-quiet__sub">Everyone&rsquo;s home. Enjoy it.</p>
                </div>
              ) : (
                <div className="kd-today__list">
                  {todayShown.map((occurrence) => (
                    <button
                      type="button"
                      key={occurrence.occurrence_id}
                      className="kd-today__event"
                      onClick={() => setOpenedId(occurrence.occurrence_id)}
                    >
                      <SpanRail occurrence={occurrence} date={payload.today.date} zone={zone} />
                      <Discs people={occurrence.people} size={34} />
                      <div>
                        <p
                          className={`kd-today__title${occurrence.is_background ? " is-background" : ""}`}
                        >
                          {occurrence.title}
                        </p>
                        <p className="kd-today__meta">
                          {/* Which day of the run, not just the hour. On the
                              second morning of a trip the hour it started is
                              yesterday's fact. */}
                          {[spanMeta(occurrence, payload.today.date, zone), occurrence.location]
                            .filter((x) => x !== null && x !== "")
                            .join(" · ")}
                        </p>
                      </div>
                    </button>
                  ))}
                  {todayHidden > 0 && (
                    <p className="kd-today__more">+{todayHidden} more today</p>
                  )}
                </div>
              )}
            </section>

            {payload.upcoming.map((day) => {
              const d = DateTime.fromISO(day.date, { zone });
              const { shown, hidden: more } = fitted(
                day.occurrences,
                UPCOMING_FITS.alone,
                UPCOMING_FITS.withMore,
              );

              return (
                <section key={day.date} className="kd-day">
                  <div className="kd-day__head">
                    <span className="kd-day__dow">{d.toFormat("ccc")}</span>
                    <span className="kd-day__num">{d.toFormat("d")}</span>

                    {(() => {
                      const w = weatherFor(day.date);
                      // The free forecast does not reach the furthest cells, so
                      // some days simply have no glyph. The row keeps its
                      // alignment either way — no hole, no reflow.
                      if (w === undefined) return null;
                      return (
                        <span className={`kd-wx${weather?.stale === true ? " is-stale" : ""}`}>
                          <WeatherGlyph kind={w.kind} size={40} />
                          <span className="kd-wx__temp kd-wx__temp--small">
                            {w.high}° <span className="kd-wx__low">{w.low}°</span>
                          </span>
                        </span>
                      );
                    })()}
                  </div>

                  {day.occurrences.length === 0 ? (
                    <p className="kd-day__clear">clear</p>
                  ) : (
                    <>
                      {shown.map((occurrence) => (
                        <button
                          type="button"
                          key={occurrence.occurrence_id}
                          className="kd-day__event"
                          onClick={() => setOpenedId(occurrence.occurrence_id)}
                        >
                          <SpanRail occurrence={occurrence} date={day.date} zone={zone} />
                          <Discs people={occurrence.people} size={30} />
                          <div className="kd-day__body">
                            <p
                              className={`kd-day__title${occurrence.is_background ? " is-background" : ""}`}
                            >
                              {occurrence.title}
                            </p>
                            <p className="kd-day__time">
                              {/* Spoken spelling, not the phone's tight one:
                                  this line is read from across the room. */}
                              {spanTime(occurrence, day.date, zone, "roomy")}
                            </p>
                          </div>
                        </button>
                      ))}
                      {more > 0 && <p className="kd-day__more">+{more} more</p>}
                    </>
                  )}
                </section>
              );
            })}
          </div>
          {stale && (
            <div className="kd-banner">
              <span className="kd-banner__dot" />
              <span className="kd-banner__text">
                Showing {fetchedAt.setZone(zone).toFormat("h:mm a").toLowerCase()} — the screen lost
                the house wifi
              </span>
              <span className="kd-banner__age">retrying · {ageMinutes} min old</span>
            </div>
          )}
          </>
        )}

        {opened !== null && (
          /*
           * Over the day, not instead of it: the schedule stays visible behind
           * so the screen never stops doing its job. Tapping anywhere closes,
           * because on a wall the whole surface is the button.
           */
          <div
            className="kd-detail"
            role="dialog"
            aria-label={opened.title}
            onClick={() => setOpenedId(null)}
          >
            <div className="kd-detail__panel" onClick={(e) => e.stopPropagation()}>
              <p className="kd-detail__when">
                {dayRangeLabel(opened.starts_at, opened.ends_at, zone)}
              </p>
              <p className="kd-detail__title">{opened.title}</p>
              <p className="kd-detail__time">{timeOf(opened, zone)}</p>
              {openedDate !== null && spanOn(opened, openedDate, zone).spans && (
                <p className="kd-detail__span">{spanMeta(opened, openedDate, zone)}</p>
              )}

              <div className="kd-detail__people">
                {opened.people.map((person) => (
                  <span key={person.id} className="kd-detail__person">
                    <span
                      className="disc"
                      style={{
                        width: 40,
                        height: 40,
                        background: person.color,
                        color: foregroundOn(person.color),
                        fontSize: 19,
                      }}
                    >
                      {initialOf(person.display_name)}
                    </span>
                    {person.display_name}
                  </span>
                ))}
              </div>

              {opened.location !== null && opened.location !== "" && (
                <p className="kd-detail__where">{opened.location}</p>
              )}

              {opened.notes !== null && opened.notes !== "" && (
                <p className="kd-detail__notes">{opened.notes}</p>
              )}

              {/* No edit here, by design: this screen has no keyboard, no
                  signed-in person, and a token anyone in the kitchen can see.
                  Reading is the whole job. */}
              <p className="kd-detail__dismiss">Touch anywhere to close</p>
            </div>
          </div>
        )}

        {weather !== null && view === "week" && (
          <footer className="kd-attribution">
            {/* A licence condition, not a courtesy: free-tier use requires
                visible credit where the data is shown — so it goes with the
                weather, and the month view, which has none, does not carry a
                credit for data it is not showing. */}
            <span>{weather.attribution.text}</span>
          </footer>
        )}
      </div>
    </div>
  );
}
